import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api';
import Modal from '../components/Modal';

const emptyProject = { customer_id: '', contact_id: '', name: '', description: '', po_number: '', po_amount: '', location: '', status: 'active', include_timesheets: true, show_invoice_period: true, project_type: 'hourly', total_cost: '', requires_daily_logs: true, billing_method: 'percentage', monthly_engineer_pay: '', monthly_invoice_amount: '', internal: false, edi_uom: '', edi_plant_code: '', edi_po_quantity: '', edi_unit_price: '', overtime_type: 'none', invoice_consolidate: false, unit_price: '' };

const NO_ENGINEER_FILTER = 'none';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyProject);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectEngineers, setProjectEngineers] = useState([]);
  const [assignLoadError, setAssignLoadError] = useState('');
  const [assignForm, setAssignForm] = useState({ user_id: '', pay_rate: '', bill_rate: '', total_payment: '', monthly_pay: '', monthly_bill: '', max_hours: '', ot_pay_rate: '', ot_bill_rate: '' });
  const [customerFilter, setCustomerFilter] = useState('');
  const [engineerFilter, setEngineerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [engineerAssignments, setEngineerAssignments] = useState([]);
  const [notifying, setNotifying] = useState(null);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [contactFilter, setContactFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [importingPo, setImportingPo] = useState(false);
  const [poLines, setPoLines] = useState([]);
  const [poLineForm, setPoLineForm] = useState({ line_number: '', description: '', engineer_id: '', edi_uom: 'HR', edi_po_quantity: '', edi_unit_price: '', po_line_amount: '' });
  const [showAddPoLine, setShowAddPoLine] = useState(false);
  const [reassignProject, setReassignProject] = useState(null);
  const [reassignTimesheets, setReassignTimesheets] = useState([]);
  const [reassignSelected, setReassignSelected] = useState([]);
  const [reassignTarget, setReassignTarget] = useState('');
  const [reassigning, setReassigning] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [p, c, u, ep] = await Promise.all([
        apiFetch('/projects'),
        apiFetch('/customers'),
        apiFetch('/users'),
        apiFetch('/engineer-projects'),
      ]);
      setProjects(p);
      setCustomers(c);
      setEngineers(u.filter((user) => user.role === 'engineer' && user.active !== 0));
      setEngineerAssignments(ep);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = useCallback(async (customerId) => {
    if (!customerId) {
      setContacts([]);
      return;
    }
    try {
      const data = await apiFetch(`/customers/${customerId}/contacts`);
      setContacts(data);
    } catch (e) {
      setContacts([]);
    }
  }, []);

  const openAdd = () => {
    setForm(emptyProject);
    setContacts([]);
    setPoLines([]);
    setError('');
    setModal('add');
  };

  const openEdit = async (project) => {
    setForm({
      ...project,
      contact_id: project.contact_id || '',
      description: project.description || '',
      po_amount: project.po_amount || '',
      include_timesheets: project.include_timesheets !== 0,
      show_invoice_period: project.show_invoice_period !== 0,
      project_type: project.project_type || 'hourly',
      total_cost: project.total_cost || '',
      requires_daily_logs: project.requires_daily_logs !== 0,
      billing_method: project.billing_method || 'percentage',
      monthly_engineer_pay: project.monthly_engineer_pay || '',
      monthly_invoice_amount: project.monthly_invoice_amount || '',
      internal: project.internal === 1,
      edi_uom: project.edi_uom || '',
      edi_plant_code: project.edi_plant_code || '',
      edi_po_quantity: project.edi_po_quantity || '',
      edi_unit_price: project.edi_unit_price || '',
      overtime_type: project.overtime_type || 'none',
      invoice_consolidate: project.invoice_consolidate === 1,
    });
    setError('');
    if (project.customer_id) {
      await loadContacts(project.customer_id);
    }
    try {
      const lines = await apiFetch(`/projects/${project.id}/po-lines`);
      setPoLines(lines);
    } catch { setPoLines([]); }
    setModal('edit');
  };

  const openAssign = async (project) => {
    setSelectedProject(project);
    setAssignForm({ user_id: '', pay_rate: '', bill_rate: '', total_payment: '', monthly_pay: '', monthly_bill: '', max_hours: '', ot_pay_rate: '', ot_bill_rate: '' });
    setAssignLoadError('');
    try {
      const engs = await apiFetch(`/projects/${project.id}/engineers`);
      setProjectEngineers(engs);
    } catch (e) {
      setProjectEngineers([]);
      setAssignLoadError(`Couldn't load the assigned engineers: ${e.message}. Close this window and try again.`);
    }
    setModal('assign');
  };

  const handleCustomerChange = async (customerId) => {
    setForm({ ...form, customer_id: customerId, contact_id: '' });
    await loadContacts(customerId);
  };

  const matchesEngineerFilter = (project) => {
    if (!engineerFilter) return true;
    const assigned = engineerAssignments.filter(ea => ea.project_id === project.id);
    if (engineerFilter === NO_ENGINEER_FILTER) return assigned.length === 0;
    return assigned.some(ea => String(ea.user_id) === engineerFilter);
  };

  const matchesFilters = (p) => {
    if (statusFilter && p.status !== statusFilter) return false;
    if (customerFilter && String(p.customer_id) !== customerFilter) return false;
    if (contactFilter && p.contact_name !== contactFilter) return false;
    if (!matchesEngineerFilter(p)) return false;
    if (dateFrom && p.created_at && p.created_at.slice(0, 10) < dateFrom) return false;
    if (dateTo && p.created_at && p.created_at.slice(0, 10) > dateTo) return false;
    return true;
  };

  const handleImportPo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportingPo(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('tt_token');
      const resp = await fetch((process.env.REACT_APP_API_URL || '') + '/api/parse-po', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || 'Failed to parse PO');
      }
      const data = await resp.json();

      // Build project name: "PO 62147937 - Description"
      const poNum = data.po_number || '';
      const desc = data.name || '';
      let projectName = '';
      if (poNum && desc) {
        const poPrefix = poNum.toUpperCase().includes('PO') ? poNum : `PO ${poNum}`;
        projectName = `${poPrefix} - ${desc}`;
      } else if (poNum) {
        projectName = poNum.toUpperCase().includes('PO') ? poNum : `PO ${poNum}`;
      } else {
        projectName = desc;
      }

      // Find or create customer contact from requester
      let contactId = '';
      const customerId = form.customer_id;
      if (data.requester_name && customerId) {
        const existing = contacts.find(c =>
          c.name.toLowerCase() === data.requester_name.toLowerCase() ||
          (c.email && data.requester_email && c.email.toLowerCase() === data.requester_email.toLowerCase())
        );
        if (existing) {
          contactId = existing.id;
        } else {
          const createResp = await apiFetch(`/customers/${customerId}/contacts`, {
            method: 'POST',
            body: {
              name: data.requester_name,
              email: data.requester_email || '',
              title: '',
              phone: '',
            },
          });
          if (createResp && createResp.id) {
            contactId = createResp.id;
            await loadContacts(customerId);
          }
        }
      }

      setForm(prev => ({
        ...prev,
        po_number: poNum || prev.po_number,
        name: projectName || prev.name,
        description: desc || prev.description,
        contact_id: contactId || prev.contact_id,
        edi_plant_code: data.edi_plant_code || prev.edi_plant_code,
        edi_uom: data.edi_uom || prev.edi_uom,
        edi_po_quantity: data.edi_po_quantity || prev.edi_po_quantity,
        edi_unit_price: data.edi_unit_price || prev.edi_unit_price,
        po_amount: data.po_amount || prev.po_amount,
        total_cost: prev.project_type === 'fixed_price' ? (data.po_amount || prev.total_cost) : prev.total_cost,
        location: data.location || prev.location,
      }));

      if (data.line_items && data.line_items.length > 1) {
        setPoLines(data.line_items.map((li, idx) => ({
          line_number: li.line_number || (idx + 1) * 10,
          description: li.description || '',
          engineer_id: null,
          edi_uom: li.uom || li.edi_uom || 'HR',
          edi_po_quantity: parseFloat(li.quantity || li.edi_po_quantity) || 0,
          edi_unit_price: parseFloat(li.unit_price || li.edi_unit_price) || 0,
          po_line_amount: (parseFloat(li.quantity || li.edi_po_quantity) || 0) * (parseFloat(li.unit_price || li.edi_unit_price) || 0),
        })));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setImportingPo(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e, confirmInactive = false) => {
    if (e) e.preventDefault();
    if (!form.name.trim() || !form.customer_id) {
      setError('Customer and project name are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = {
        ...form,
        contact_id: form.contact_id || null,
        po_amount: form.po_amount ? parseFloat(form.po_amount) : 0,
        total_cost: form.total_cost ? parseFloat(form.total_cost) : 0,
        monthly_engineer_pay: form.monthly_engineer_pay ? parseFloat(form.monthly_engineer_pay) : 0,
        monthly_invoice_amount: form.monthly_invoice_amount ? parseFloat(form.monthly_invoice_amount) : 0,
        ...(confirmInactive ? { confirm_inactive: true } : {}),
      };
      let projectId;
      if (modal === 'add') {
        const result = await apiFetch('/projects', { method: 'POST', body });
        projectId = result.id;
      } else {
        projectId = form.id;
        try {
          await apiFetch(`/projects/${form.id}`, { method: 'PUT', body });
        } catch (err) {
          if (err.message && err.message.includes('open invoice(s)')) {
            if (window.confirm(err.message)) {
              await handleSubmit(null, true);
              return;
            } else {
              setSaving(false);
              return;
            }
          }
          throw err;
        }
      }
      if (poLines.length > 0 && projectId) {
        await apiFetch(`/projects/${projectId}/po-lines/bulk`, {
          method: 'POST',
          body: { lines: poLines },
        });
      } else if (poLines.length === 0 && projectId && modal === 'edit') {
        await apiFetch(`/projects/${projectId}/po-lines/bulk`, {
          method: 'POST',
          body: { lines: [] },
        });
      }
      await loadData();
      setModal(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const openReassign = async (project) => {
    setReassignProject(project);
    setReassignTarget('');
    setReassignSelected([]);
    setReassigning(false);
    try {
      const ts = await apiFetch(`/projects/${project.id}/timesheets-summary`);
      setReassignTimesheets(ts);
      setReassignSelected(ts.map(t => t.id));
    } catch { setReassignTimesheets([]); }
    setModal('reassign');
  };

  const handleReassign = async () => {
    if (!reassignTarget || reassignSelected.length === 0) return;
    const targetName = projects.find(p => String(p.id) === String(reassignTarget))?.name || reassignTarget;
    if (!window.confirm(`Move ${reassignSelected.length} timesheet(s) to "${targetName}"?\n\nEngineer assignments (rates) will also be copied to the target project.`)) return;
    setReassigning(true);
    try {
      const result = await apiFetch(`/projects/${reassignProject.id}/reassign-timesheets`, {
        method: 'POST',
        body: { target_project_id: parseInt(reassignTarget), timesheet_ids: reassignSelected },
      });
      alert(`Moved ${result.timesheets_moved} timesheet(s). ${result.assignments_copied > 0 ? `Copied ${result.assignments_copied} engineer assignment(s).` : ''}`);
      setModal(null);
      await loadData();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setReassigning(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await apiFetch(`/projects/${id}`, { method: 'DELETE' });
      await loadData();
    } catch (e) {
      alert('Cannot delete: ' + e.message);
    }
  };

  const handleAssignEngineer = async (e) => {
    e.preventDefault();
    const isFixedPrice = selectedProject?.project_type === 'fixed_price';
    const isFixedMonthly = selectedProject?.project_type === 'fixed_monthly';
    const isPieceRate = selectedProject?.project_type === 'piece_rate';
    if (isPieceRate) {
      if (!assignForm.user_id || !assignForm.pay_rate) {
        setError('Engineer and pay rate per unit are required');
        return;
      }
    } else if (isFixedPrice) {
      if (!assignForm.user_id || !assignForm.total_payment) {
        setError('Engineer and total payment are required');
        return;
      }
    } else if (isFixedMonthly) {
      if (!assignForm.user_id || !assignForm.monthly_pay || !assignForm.monthly_bill) {
        setError('Engineer, monthly pay, and monthly bill are required');
        return;
      }
    } else {
      if (!assignForm.user_id || !assignForm.pay_rate || !assignForm.bill_rate) {
        setError('All fields are required');
        return;
      }
    }
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/projects/${selectedProject.id}/engineers`, {
        method: 'POST',
        body: {
          user_id: parseInt(assignForm.user_id),
          pay_rate: isPieceRate ? parseFloat(assignForm.pay_rate) : (isFixedPrice || isFixedMonthly ? 0 : parseFloat(assignForm.pay_rate)),
          bill_rate: isFixedPrice || isFixedMonthly || isPieceRate ? 0 : parseFloat(assignForm.bill_rate),
          total_payment: isFixedPrice ? parseFloat(assignForm.total_payment) : 0,
          monthly_pay: isFixedMonthly ? parseFloat(assignForm.monthly_pay) : 0,
          monthly_bill: isFixedMonthly ? parseFloat(assignForm.monthly_bill) : 0,
          max_hours: (!isFixedPrice && !isFixedMonthly && !isPieceRate && assignForm.max_hours) ? parseFloat(assignForm.max_hours) : 0,
          ot_pay_rate: (!isFixedPrice && !isFixedMonthly && !isPieceRate && assignForm.ot_pay_rate) ? parseFloat(assignForm.ot_pay_rate) : 0,
          ot_bill_rate: (!isFixedPrice && !isFixedMonthly && !isPieceRate && assignForm.ot_bill_rate) ? parseFloat(assignForm.ot_bill_rate) : 0,
        },
      });
      const engs = await apiFetch(`/projects/${selectedProject.id}/engineers`);
      setProjectEngineers(engs);
      setAssignForm({ user_id: '', pay_rate: '', bill_rate: '', total_payment: '', monthly_pay: '', monthly_bill: '', max_hours: '', ot_pay_rate: '', ot_bill_rate: '' });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUnassignEngineer = async (userId) => {
    if (!window.confirm('Remove this engineer from the project?')) return;
    try {
      await apiFetch(`/projects/${selectedProject.id}/engineers/${userId}`, { method: 'DELETE' });
      const engs = await apiFetch(`/projects/${selectedProject.id}/engineers`);
      setProjectEngineers(engs);
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const [emailPreview, setEmailPreview] = useState(null);

  const handleNotifyEngineer = async (userId) => {
    setNotifying(userId);
    try {
      const result = await apiFetch(`/projects/${selectedProject.id}/notify-engineer`, {
        method: 'POST',
        body: { user_id: userId, preview: true },
      });
      setEmailPreview({ ...result, userId });
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setNotifying(null);
    }
  };

  const handleSendNotification = async () => {
    if (!emailPreview) return;
    try {
      const result = await apiFetch(`/projects/${selectedProject.id}/notify-engineer`, {
        method: 'POST',
        body: { user_id: emailPreview.userId, preview: false },
      });
      alert(result.message);
      setEmailPreview(null);
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'created_at' ? 'desc' : 'asc');
    }
  };

  const sortIndicator = (field) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  if (loading) return <div style={{ padding: 40, color: '#94a3b8' }}>Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Projects</div>
          <div className="page-subtitle">Manage projects and engineer assignments</div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Project</button>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Status:</span>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ width: 'auto', minWidth: 130 }}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Customer:</span>
            <select
              className="form-select"
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              style={{ width: 'auto', minWidth: 200 }}
            >
              <option value="">All Customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Contact:</span>
            <select
              className="form-select"
              value={contactFilter}
              onChange={(e) => setContactFilter(e.target.value)}
              style={{ width: 'auto', minWidth: 180 }}
            >
              <option value="">All Contacts</option>
              {[...new Set(projects.map(p => p.contact_name).filter(Boolean))].sort().map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Engineer:</span>
            <select
              className="form-select"
              value={engineerFilter}
              onChange={(e) => setEngineerFilter(e.target.value)}
              style={{ width: 'auto', minWidth: 180 }}
            >
              <option value="">All Engineers</option>
              <option value={NO_ENGINEER_FILTER}>No engineer assigned</option>
              {engineers.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Created:</span>
            <input type="date" className="form-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ width: 140, padding: '4px 8px', fontSize: 13 }} />
            <span style={{ fontSize: 13, color: '#64748b' }}>to</span>
            <input type="date" className="form-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ width: 140, padding: '4px 8px', fontSize: 13 }} />
            {(dateFrom || dateTo) && <button className="btn btn-secondary btn-sm" onClick={() => { setDateFrom(''); setDateTo(''); }} style={{ padding: '2px 8px', fontSize: 11 }}>Clear</button>}
          </div>
          <span style={{ fontSize: 13, color: '#94a3b8', marginLeft: 'auto' }}>
            {projects.filter(matchesFilters).length} of {projects.length} projects
          </span>
        </div>
      </div>

      <div className="card">
        {projects.length === 0 ? (
          <div className="empty-state">
            <h3>No projects yet</h3>
            <p>Add your first project to get started.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>Project{sortIndicator('name')}</th>
                  <th>Type</th>
                  <th onClick={() => handleSort('customer')} style={{ cursor: 'pointer', userSelect: 'none' }}>Customer{sortIndicator('customer')}</th>
                  <th onClick={() => handleSort('contact')} style={{ cursor: 'pointer', userSelect: 'none' }}>Contact{sortIndicator('contact')}</th>
                  <th onClick={() => handleSort('po_number')} style={{ cursor: 'pointer', userSelect: 'none' }}>PO #{sortIndicator('po_number')}</th>
                  <th>Budget</th>
                  <th>Billed</th>
                  <th>Paid</th>
                  <th>Unbilled</th>
                  <th>Remaining</th>
                  <th onClick={() => handleSort('progress')} style={{ cursor: 'pointer', userSelect: 'none' }}>Progress{sortIndicator('progress')}</th>
                  <th>Status</th>
                  <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer', userSelect: 'none' }}>Created{sortIndicator('created_at')}</th>
                  <th style={{ width: 180 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.filter(matchesFilters).sort((a, b) => {
                  let aVal, bVal;
                  if (sortField === 'created_at') {
                    aVal = a.created_at || '';
                    bVal = b.created_at || '';
                  } else if (sortField === 'customer') {
                    aVal = (a.customer_name || '').toLowerCase();
                    bVal = (b.customer_name || '').toLowerCase();
                  } else if (sortField === 'po_number') {
                    aVal = (a.po_number || '').toLowerCase();
                    bVal = (b.po_number || '').toLowerCase();
                  } else if (sortField === 'contact') {
                    aVal = (a.contact_name || '').toLowerCase();
                    bVal = (b.contact_name || '').toLowerCase();
                  } else if (sortField === 'progress') {
                    const aBudget = a.project_type === 'fixed_price' ? (a.total_cost || 0) : (a.po_amount || 0);
                    const bBudget = b.project_type === 'fixed_price' ? (b.total_cost || 0) : (b.po_amount || 0);
                    aVal = aBudget > 0 ? ((a.amount_billed || 0) / aBudget) : 0;
                    bVal = bBudget > 0 ? ((b.amount_billed || 0) / bBudget) : 0;
                  } else {
                    aVal = (a.name || '').toLowerCase();
                    bVal = (b.name || '').toLowerCase();
                  }
                  if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
                  if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
                  return 0;
                }).map((p) => {
                  const isFixedPrice = p.project_type === 'fixed_price';
                  const isFixedMonthly = p.project_type === 'fixed_monthly';
                  const isPieceRate = p.project_type === 'piece_rate';
                  const budget = isFixedPrice ? (p.total_cost || 0) : (p.po_amount || 0);
                  const billed = p.amount_billed || 0;
                  const paid = p.amount_paid || 0;
                  const unbilled = p.amount_unbilled || 0;
                  const remaining = budget - billed - unbilled;
                  const pct = budget > 0 ? ((billed + unbilled) / budget) * 100 : 0;
                  const cls = pct >= 90 ? 'progress-danger' : pct >= 70 ? 'progress-warn' : 'progress-good';
                  const fullyPaid = budget > 0 && billed >= budget && paid >= billed;
                  return (
                    <tr key={p.id}>
                      <td><strong>{p.name}</strong><br /><span style={{ fontSize: 12, color: '#94a3b8' }}>{p.location || ''}</span></td>
                      <td>
                        <span className={`badge ${isFixedPrice || isPieceRate ? 'badge-fixed' : isFixedMonthly ? 'badge-fixed' : 'badge-hourly'}`} style={{ fontSize: 11 }}>
                          {isPieceRate ? 'Piece Rate' : isFixedPrice ? 'Fixed Price' : isFixedMonthly ? 'Fixed Monthly' : 'Hourly'}
                        </span>
                        {p.internal === 1 && (
                          <span className="badge" style={{ fontSize: 10, marginLeft: 4, background: '#dbeafe', color: '#1d4ed8' }}>Internal</span>
                        )}
                      </td>
                      <td>{p.customer_name}</td>
                      <td>{p.contact_name || '—'}</td>
                      <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>{p.po_number || '—'}</td>
                      <td>${budget.toLocaleString()}</td>
                      <td>${billed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td style={{ color: paid >= billed && billed > 0 ? '#16a34a' : paid > 0 ? '#d97706' : undefined }}>
                        ${paid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ color: unbilled > 0 ? '#d97706' : undefined, fontFamily: 'DM Mono, monospace', fontSize: 13 }}>
                        {unbilled > 0 ? `$${unbilled.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                      </td>
                      <td style={{ color: remaining < 0 ? '#ef4444' : undefined }}>
                        ${remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ minWidth: 100 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div className="progress-bar" style={{ flex: 1 }}>
                            <div className={`progress-fill ${cls}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                          </div>
                          <span style={{ fontSize: 11, color: '#64748b', minWidth: 30 }}>{pct.toFixed(0)}%</span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge badge-${p.status === 'active' ? 'active' : 'inactive'}`}>{p.status}</span>
                        {fullyPaid && p.status === 'active' && (
                          <span title="Fully billed and paid — safe to close" style={{ marginLeft: 4, fontSize: 11, color: '#16a34a', fontWeight: 600 }}>Ready</span>
                        )}
                      </td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>
                        {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <button className="btn btn-secondary btn-sm" onClick={() => openAssign(p)} style={{ marginRight: 4 }}>Engineers</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(p)} style={{ marginRight: 4 }}>Edit</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => openReassign(p)} style={{ marginRight: 4 }}>Reassign</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p.id)}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(modal === 'add' || modal === 'edit') && (
        <Modal
          title={modal === 'add' ? 'Add Project' : 'Edit Project'}
          onClose={() => setModal(null)}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : modal === 'add' ? 'Add Project' : 'Save Changes'}
              </button>
            </>
          }
        >
          <form onSubmit={handleSubmit}>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group">
              <label className="form-label">Customer *</label>
              <select
                className="form-select"
                value={form.customer_id}
                onChange={(e) => handleCustomerChange(e.target.value)}
              >
                <option value="">Select a customer...</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Customer Contact</label>
              <select
                className="form-select"
                value={form.contact_id}
                onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
                disabled={!form.customer_id}
              >
                <option value="">Select a contact...</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.title ? ` - ${c.title}` : ''}</option>
                ))}
              </select>
              <div className="form-hint">The main person from the customer for this project (appears on invoices)</div>
            </div>
            <div className="form-group">
              <label className="form-label">Project Name *</label>
              <input
                className="form-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Enter project name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Invoice Description</label>
              <textarea
                className="form-textarea"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Description that appears on invoices (e.g., 'Engineering Labor Hours')"
                rows={2}
              />
              <div className="form-hint">This description will appear on invoice line items</div>
            </div>
            <div className="form-group">
              <label className="form-label">Project Type</label>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="project_type"
                    value="hourly"
                    checked={form.project_type === 'hourly'}
                    onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                  />
                  <span>Hourly</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="project_type"
                    value="fixed_monthly"
                    checked={form.project_type === 'fixed_monthly'}
                    onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                  />
                  <span>Fixed Monthly</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="project_type"
                    value="fixed_price"
                    checked={form.project_type === 'fixed_price'}
                    onChange={(e) => setForm({ ...form, project_type: e.target.value, total_cost: form.total_cost || form.po_amount })}
                  />
                  <span>Fixed Price</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="project_type"
                    value="piece_rate"
                    checked={form.project_type === 'piece_rate'}
                    onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                  />
                  <span>Piece Rate</span>
                </label>
              </div>
              <div className="form-hint">
                {form.project_type === 'hourly'
                  ? 'Engineers bill by the hour with time entries'
                  : form.project_type === 'fixed_monthly'
                  ? 'Fixed monthly pay and billing — timesheets required for detail only'
                  : form.project_type === 'piece_rate'
                  ? 'Engineers bill per unit produced at a configured unit price'
                  : 'Engineers bill a percentage of their total payment'}
              </div>
            </div>
            {form.project_type === 'hourly' && (
            <div className="form-group">
              <label className="form-label">Overtime</label>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="overtime_type"
                    value="none"
                    checked={form.overtime_type === 'none'}
                    onChange={(e) => setForm({ ...form, overtime_type: e.target.value })}
                  />
                  <span>None</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="overtime_type"
                    value="weekly_40"
                    checked={form.overtime_type === 'weekly_40'}
                    onChange={(e) => setForm({ ...form, overtime_type: e.target.value })}
                  />
                  <span>Weekly (&gt;40 hrs)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="overtime_type"
                    value="daily_8"
                    checked={form.overtime_type === 'daily_8'}
                    onChange={(e) => setForm({ ...form, overtime_type: e.target.value })}
                  />
                  <span>Daily (&gt;8 hrs)</span>
                </label>
              </div>
              <div className="form-hint">
                {form.overtime_type === 'none'
                  ? 'No overtime rates applied'
                  : form.overtime_type === 'weekly_40'
                  ? 'OT rates apply when an engineer works more than 40 hours in a week'
                  : 'OT rates apply when an engineer works more than 8 hours in a day'}
              </div>
            </div>
            )}
            {form.customer_id && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ cursor: importingPo ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, background: '#16a34a', color: '#fff', padding: '8px 16px', borderRadius: 6, fontWeight: 600, fontSize: 13 }}>
                  {importingPo ? 'Parsing...' : 'Import from PO PDF'}
                  <input
                    type="file"
                    accept=".pdf"
                    style={{ display: 'none' }}
                    onChange={handleImportPo}
                    disabled={importingPo}
                  />
                </label>
                <span style={{ fontSize: 13, color: '#4b5563' }}>Upload a PO PDF to auto-fill project fields</span>
              </div>
            )}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">PO Number</label>
                <input
                  className="form-input"
                  value={form.po_number}
                  onChange={(e) => setForm({ ...form, po_number: e.target.value })}
                  placeholder="PO-12345"
                />
              </div>
              <div className="form-group">
                <label className="form-label">{form.project_type === 'fixed_price' ? 'Total Cost ($)' : 'PO Amount ($)'}</label>
                {form.project_type === 'fixed_price' ? (
                  <input
                    className="form-input"
                    type="number"
                    step="0.01"
                    value={form.total_cost}
                    onChange={(e) => setForm({ ...form, total_cost: e.target.value })}
                    placeholder="0.00"
                  />
                ) : (
                  <input
                    className="form-input"
                    type="number"
                    step="0.01"
                    value={form.po_amount}
                    onChange={(e) => setForm({ ...form, po_amount: e.target.value })}
                    placeholder="0.00"
                  />
                )}
                <div className="form-hint">
                  {form.project_type === 'fixed_price'
                    ? 'Total amount to bill the customer for this project'
                    : form.project_type === 'piece_rate'
                    ? 'Total project budget (unit price × quantity)'
                    : 'Budget limit for hourly billing'}
                </div>
              </div>
            </div>
            {form.project_type === 'fixed_price' && (
              <div style={{ background: '#f0f9ff', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 12 }}>Billing Method</div>
                <div className="form-group">
                  <select
                    className="form-select"
                    value={form.billing_method}
                    onChange={(e) => setForm({ ...form, billing_method: e.target.value })}
                  >
                    <option value="percentage">Percentage-based (engineer enters %)</option>
                    <option value="monthly_installment">Monthly Installment (fixed monthly amounts)</option>
                  </select>
                </div>
                {form.billing_method === 'monthly_installment' && (
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Monthly Engineer Pay ($)</label>
                      <input
                        className="form-input"
                        type="number"
                        step="0.01"
                        value={form.monthly_engineer_pay}
                        onChange={(e) => setForm({ ...form, monthly_engineer_pay: e.target.value })}
                        placeholder="0.00"
                      />
                      <div className="form-hint">Amount paid to engineer each month</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Monthly Invoice Amount ($)</label>
                      <input
                        className="form-input"
                        type="number"
                        step="0.01"
                        value={form.monthly_invoice_amount}
                        onChange={(e) => setForm({ ...form, monthly_invoice_amount: e.target.value })}
                        placeholder="0.00"
                      />
                      <div className="form-hint">Amount billed to customer each month</div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {form.project_type === 'piece_rate' && (
              <div style={{ background: '#f0f9ff', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 12 }}>Piece Rate Configuration</div>
                <div className="form-group">
                  <label className="form-label">Unit Price ($)</label>
                  <input
                    className="form-input"
                    type="number"
                    step="0.01"
                    value={form.unit_price}
                    onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                    placeholder="0.00"
                  />
                  <div className="form-hint">
                    Customer price per unit produced
                    {form.po_amount > 0 && form.unit_price > 0 &&
                      ` — Total quantity: ${Math.floor(parseFloat(form.po_amount) / parseFloat(form.unit_price))} units`}
                  </div>
                </div>
              </div>
            )}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Location</label>
                <input
                  className="form-input"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Job site location"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            {!!customers.find(c => String(c.id) === String(form.customer_id) && c.edi_invoicing) && (
              <>
                <div className="form-row" style={{ marginTop: 8 }}>
                  {poLines.length === 0 && (
                    <div className="form-group">
                      <label className="form-label">EDI Unit of Measure</label>
                      <select
                        className="form-select"
                        value={form.edi_uom}
                        onChange={(e) => setForm({ ...form, edi_uom: e.target.value })}
                      >
                        <option value="">None (no EDI)</option>
                        <option value="HR">HR - Hourly</option>
                        <option value="MON">MON - Monthly</option>
                        <option value="EA">EA - Each</option>
                        <option value="LO">LO - Lot</option>
                        <option value="PCE">PCE - Piece</option>
                      </select>
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">EDI Plant Code</label>
                    <input
                      className="form-input"
                      value={form.edi_plant_code}
                      onChange={(e) => setForm({ ...form, edi_plant_code: e.target.value })}
                      placeholder="e.g. 4636"
                    />
                  </div>
                </div>
                {poLines.length === 0 && (
                  <>
                    <div className="form-row" style={{ marginTop: 8 }}>
                      <div className="form-group">
                        <label className="form-label">EDI PO Quantity</label>
                        <input
                          className="form-input"
                          type="number"
                          value={form.edi_po_quantity}
                          onChange={(e) => setForm({ ...form, edi_po_quantity: e.target.value })}
                          placeholder="e.g. 4725"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">EDI Unit Price</label>
                        <input
                          className="form-input"
                          type="number"
                          step="0.01"
                          value={form.edi_unit_price}
                          onChange={(e) => setForm({ ...form, edi_unit_price: e.target.value })}
                          placeholder="e.g. 1.00"
                        />
                      </div>
                    </div>
                    <div className="form-hint">UOM, plant code, PO quantity and unit price from the FCA PO — used in EDI 810 generation</div>
                  </>
                )}
                <div style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong style={{ fontSize: 14 }}>PO Line Items</strong>
                    <span style={{ fontSize: 12, color: '#64748b' }}>For multi-line POs (e.g. one line per engineer)</span>
                  </div>
                  {poLines.length > 0 && (
                    <div className="table-wrap" style={{ marginBottom: 8 }}>
                      <table style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th>Line #</th>
                            <th>Description</th>
                            <th>Engineer</th>
                            <th>UOM</th>
                            <th>Qty</th>
                            <th>Unit Price</th>
                            <th>Amount</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {poLines.map((pl, idx) => (
                            <tr key={idx}>
                              <td>{pl.line_number}</td>
                              <td>{pl.description || '—'}</td>
                              <td>
                                <select
                                  className="form-select"
                                  style={{ fontSize: 12, padding: '2px 4px', minWidth: 120 }}
                                  value={pl.engineer_id || ''}
                                  onChange={(e) => {
                                    const updated = poLines.map((l, i) => i === idx ? { ...l, engineer_id: e.target.value ? parseInt(e.target.value) : null } : l);
                                    setPoLines(updated);
                                  }}
                                >
                                  <option value="">— Select —</option>
                                  {engineers.filter(u => u.role === 'engineer').map(u => (
                                    <option key={u.id} value={u.id}>{u.name}</option>
                                  ))}
                                </select>
                              </td>
                              <td>{pl.edi_uom || '—'}</td>
                              <td style={{ textAlign: 'right' }}>{pl.edi_po_quantity || '—'}</td>
                              <td style={{ textAlign: 'right' }}>{pl.edi_unit_price ? `$${parseFloat(pl.edi_unit_price).toFixed(2)}` : '—'}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>{pl.po_line_amount ? `$${parseFloat(pl.po_line_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}</td>
                              <td>
                                <button type="button" className="btn btn-secondary" style={{ fontSize: 11, padding: '2px 6px', color: '#dc2626' }}
                                  onClick={() => setPoLines(poLines.filter((_, i) => i !== idx))}>Remove</button>
                              </td>
                            </tr>
                          ))}
                          <tr style={{ fontWeight: 600, background: '#f8fafc' }}>
                            <td colSpan="6" style={{ textAlign: 'right' }}>Total:</td>
                            <td style={{ textAlign: 'right' }}>${poLines.reduce((s, l) => s + (parseFloat(l.po_line_amount) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                  {!showAddPoLine ? (
                    <button type="button" className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }}
                      onClick={() => setShowAddPoLine(true)}>+ Add Line Manually</button>
                  ) : (
                    <div style={{ background: '#f8fafc', borderRadius: 6, padding: 10, marginTop: 4 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Add Line Item</span>
                        <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#94a3b8' }}
                          onClick={() => setShowAddPoLine(false)}>Cancel</button>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div>
                          <label style={{ fontSize: 11, color: '#64748b' }}>Line #</label>
                          <input className="form-input" style={{ width: 60, fontSize: 12, padding: '4px 6px' }} type="number"
                            value={poLineForm.line_number} onChange={e => setPoLineForm({ ...poLineForm, line_number: e.target.value })} placeholder="10" />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#64748b' }}>Description</label>
                          <input className="form-input" style={{ width: 140, fontSize: 12, padding: '4px 6px' }}
                            value={poLineForm.description} onChange={e => setPoLineForm({ ...poLineForm, description: e.target.value })} placeholder="Engineer name / service" />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#64748b' }}>Engineer</label>
                          <select className="form-select" style={{ fontSize: 12, padding: '4px 6px', minWidth: 120 }}
                            value={poLineForm.engineer_id} onChange={e => setPoLineForm({ ...poLineForm, engineer_id: e.target.value })}>
                            <option value="">— None —</option>
                            {engineers.filter(u => u.role === 'engineer').map(u => (
                              <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#64748b' }}>UOM</label>
                          <select className="form-select" style={{ fontSize: 12, padding: '4px 6px', width: 70 }}
                            value={poLineForm.edi_uom} onChange={e => setPoLineForm({ ...poLineForm, edi_uom: e.target.value })}>
                            <option value="HR">HR</option>
                            <option value="LO">LO</option>
                            <option value="EA">EA</option>
                            <option value="MON">MON</option>
                            <option value="PCE">PCE</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#64748b' }}>Qty</label>
                          <input className="form-input" style={{ width: 70, fontSize: 12, padding: '4px 6px' }} type="number"
                            value={poLineForm.edi_po_quantity} onChange={e => setPoLineForm({ ...poLineForm, edi_po_quantity: e.target.value })} placeholder="1" />
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: '#64748b' }}>Unit Price</label>
                          <input className="form-input" style={{ width: 100, fontSize: 12, padding: '4px 6px' }} type="number" step="0.01"
                            value={poLineForm.edi_unit_price} onChange={e => setPoLineForm({ ...poLineForm, edi_unit_price: e.target.value })} placeholder="192000.00" />
                        </div>
                        <button type="button" className="btn btn-primary" style={{ fontSize: 12, padding: '4px 10px' }}
                          onClick={() => {
                            if (!poLineForm.line_number) return;
                            if (poLines.some(l => String(l.line_number) === String(poLineForm.line_number))) {
                              alert(`Line number ${poLineForm.line_number} already exists`);
                              return;
                            }
                            const qty = parseFloat(poLineForm.edi_po_quantity) || 0;
                            const price = parseFloat(poLineForm.edi_unit_price) || 0;
                            setPoLines([...poLines, {
                              line_number: parseInt(poLineForm.line_number),
                              description: poLineForm.description,
                              engineer_id: poLineForm.engineer_id ? parseInt(poLineForm.engineer_id) : null,
                              edi_uom: poLineForm.edi_uom,
                              edi_po_quantity: qty,
                              edi_unit_price: price,
                              po_line_amount: qty * price,
                            }].sort((a, b) => a.line_number - b.line_number));
                            setPoLineForm({ line_number: '', description: '', engineer_id: '', edi_uom: 'HR', edi_po_quantity: '', edi_unit_price: '', po_line_amount: '' });
                            setShowAddPoLine(false);
                          }}>Add</button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            <div className="form-group" style={{ marginTop: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.include_timesheets}
                  onChange={(e) => setForm({ ...form, include_timesheets: e.target.checked })}
                  style={{ width: 18, height: 18 }}
                />
                <span>Include timesheets with invoice emails</span>
              </label>
              <div className="form-hint">When checked, emailed invoices will include detailed timesheet reports</div>
            </div>
            <div className="form-group" style={{ marginTop: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.show_invoice_period}
                  onChange={(e) => setForm({ ...form, show_invoice_period: e.target.checked })}
                  style={{ width: 18, height: 18 }}
                />
                <span>Show billing period dates on invoices</span>
              </label>
              <div className="form-hint">Uncheck to leave the date range off invoice line descriptions and the PDF file name</div>
            </div>
            <div className="form-group" style={{ marginTop: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.invoice_consolidate}
                  onChange={(e) => setForm({ ...form, invoice_consolidate: e.target.checked })}
                  style={{ width: 18, height: 18 }}
                />
                <span>Consolidate invoice line items</span>
              </label>
              <div className="form-hint">When checked, all engineers are combined into a single line item on the invoice instead of one line per engineer</div>
            </div>
            {(form.project_type === 'hourly' || form.project_type === 'fixed_monthly') && (
              <div className="form-group" style={{ marginTop: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.requires_daily_logs}
                    onChange={(e) => setForm({ ...form, requires_daily_logs: e.target.checked })}
                    style={{ width: 18, height: 18 }}
                  />
                  <span>Requires daily time logs</span>
                </label>
                <div className="form-hint">When unchecked, engineers can submit monthly hour totals instead of daily logs</div>
              </div>
            )}
            <div className="form-group" style={{ marginTop: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.internal}
                  onChange={(e) => setForm({ ...form, internal: e.target.checked })}
                  style={{ width: 18, height: 18 }}
                />
                <span>Internal Project</span>
              </label>
              <div className="form-hint">Internal projects are not invoiced to customers but engineers still get paid for their time</div>
            </div>
          </form>
        </Modal>
      )}

      {modal === 'assign' && selectedProject && (
        <Modal
          title={`Engineers - ${selectedProject.name}`}
          onClose={() => setModal(null)}
          footer={<button className="btn btn-secondary" onClick={() => setModal(null)}>Close</button>}
        >
          {error && <div className="alert alert-error">{error}</div>}

          <div style={{ marginBottom: 20 }}>
            <div className="card-title" style={{ fontSize: 14 }}>
              Assigned Engineers
              {selectedProject.project_type === 'fixed_price' && (
                <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 8 }}>(Fixed Price Project)</span>
              )}
              {selectedProject.project_type === 'fixed_monthly' && (
                <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 8 }}>(Fixed Monthly Project)</span>
              )}
              {selectedProject.project_type === 'piece_rate' && (
                <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 8 }}>(Piece Rate Project)</span>
              )}
            </div>
            {assignLoadError ? (
              <div className="alert alert-error">{assignLoadError}</div>
            ) : projectEngineers.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: 14 }}>No engineers assigned yet.</p>
            ) : (
              <div>
                {projectEngineers.map((eng) => (
                  <div key={eng.user_id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 10, background: '#f8fafc' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <strong>{eng.name}</strong>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{eng.engineer_id || eng.email}</div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', textAlign: 'right' }}>
                        {selectedProject.project_type === 'fixed_price'
                          ? `$${(eng.total_payment || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : selectedProject.project_type === 'fixed_monthly'
                            ? `$${(eng.monthly_pay || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo`
                            : selectedProject.project_type === 'piece_rate'
                              ? `$${eng.pay_rate?.toFixed(2) || '0.00'}/unit`
                              : `$${eng.pay_rate?.toFixed(2) || '0.00'}/hr`
                        }
                        {selectedProject.project_type === 'fixed_monthly' && (
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>Bill: ${(eng.monthly_bill || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo</div>
                        )}
                        {selectedProject.project_type === 'hourly' && (
                          <>
                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>Bill: ${eng.bill_rate?.toFixed(2) || '0.00'}/hr</div>
                            {eng.max_hours > 0 && (
                              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 400 }}>Max: {eng.max_hours} hrs</div>
                            )}
                            {selectedProject.overtime_type && selectedProject.overtime_type !== 'none' && (eng.ot_pay_rate > 0 || eng.ot_bill_rate > 0) && (
                              <div style={{ fontSize: 11, color: '#d97706', fontWeight: 400 }}>OT: ${eng.ot_pay_rate?.toFixed(2)}/hr pay, ${eng.ot_bill_rate?.toFixed(2)}/hr bill</div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleNotifyEngineer(eng.user_id)}
                        disabled={notifying === eng.user_id}
                      >
                        {notifying === eng.user_id ? 'Sending...' : 'Send Assignment Email'}
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleUnassignEngineer(eng.user_id)}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedProject.project_type === 'fixed_price' && projectEngineers.length > 0 && (() => {
            const totalAssigned = projectEngineers.reduce((sum, eng) => sum + (eng.total_payment || 0), 0);
            const projectBudget = selectedProject.total_cost || 0;
            const remaining = projectBudget - totalAssigned;
            const over = remaining < 0;
            return (
              <div style={{ background: over ? '#fef2f2' : '#f0fdf4', border: `1px solid ${over ? '#fecaca' : '#bbf7d0'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span>Project Total:</span>
                  <strong>${projectBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4 }}>
                  <span>Total Assigned:</span>
                  <strong>${totalAssigned.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4, color: over ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                  <span>{over ? 'Over Budget:' : 'Remaining:'}</span>
                  <span>{over ? '-' : ''}${Math.abs(remaining).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            );
          })()}

          {selectedProject.project_type === 'hourly' && selectedProject.po_amount > 0 && (() => {
            const avgBillRate = projectEngineers.length > 0
              ? projectEngineers.reduce((sum, eng) => sum + (eng.bill_rate || 0), 0) / projectEngineers.length
              : 0;
            const totalBudgetHours = avgBillRate > 0 ? selectedProject.po_amount / avgBillRate : 0;
            const totalAssignedHours = projectEngineers.reduce((sum, eng) => sum + (eng.max_hours || 0), 0);
            const remainingHours = totalBudgetHours - totalAssignedHours;
            const over = totalAssignedHours > 0 && remainingHours < 0;
            const pending = assignForm.max_hours ? parseFloat(assignForm.max_hours) || 0 : 0;
            const remainingAfterPending = remainingHours - pending;
            return (
              <div style={{ background: over ? '#fef2f2' : '#f0fdf4', border: `1px solid ${over ? '#fecaca' : '#bbf7d0'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span>PO Budget:</span>
                  <strong>${selectedProject.po_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                </div>
                {totalBudgetHours > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4 }}>
                    <span>Total Hours Available (avg ${avgBillRate.toFixed(2)}/hr):</span>
                    <strong>{totalBudgetHours.toFixed(1)} hrs</strong>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4 }}>
                  <span>Hours Assigned:</span>
                  <strong>{totalAssignedHours > 0 ? `${totalAssignedHours.toFixed(1)} hrs` : 'No limits set'}</strong>
                </div>
                {totalAssignedHours > 0 && totalBudgetHours > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4, color: over ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                    <span>{over ? 'Over Allocated:' : 'Hours Remaining:'}</span>
                    <span>{over ? '-' : ''}{Math.abs(remainingHours).toFixed(1)} hrs</span>
                  </div>
                )}
                {pending > 0 && totalBudgetHours > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4, color: remainingAfterPending < 0 ? '#dc2626' : '#64748b', fontStyle: 'italic' }}>
                    <span>After this assignment:</span>
                    <span>{remainingAfterPending.toFixed(1)} hrs</span>
                  </div>
                )}
              </div>
            );
          })()}

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            <div className="card-title" style={{ fontSize: 14 }}>Add Engineer</div>
            <form onSubmit={handleAssignEngineer}>
              <div className="form-group">
                <label className="form-label">Engineer</label>
                <select
                  className="form-select"
                  value={assignForm.user_id}
                  onChange={(e) => setAssignForm({ ...assignForm, user_id: e.target.value })}
                >
                  <option value="">Select an engineer...</option>
                  {engineers
                    .filter((eng) => !projectEngineers.some((pe) => pe.user_id === eng.id))
                    .map((eng) => (
                      <option key={eng.id} value={eng.id}>{eng.name}</option>
                    ))}
                </select>
              </div>
              {selectedProject.project_type === 'piece_rate' ? (
                <div className="form-group">
                  <label className="form-label">Pay Rate ($/unit)</label>
                  <input
                    className="form-input"
                    type="number"
                    step="0.01"
                    value={assignForm.pay_rate}
                    onChange={(e) => setAssignForm({ ...assignForm, pay_rate: e.target.value })}
                    placeholder="0.00"
                  />
                  <div className="form-hint">Amount paid to engineer per unit produced</div>
                </div>
              ) : selectedProject.project_type === 'fixed_price' ? (
                <div className="form-group">
                  <label className="form-label">Total Payment ($)</label>
                  <input
                    className="form-input"
                    type="number"
                    step="0.01"
                    value={assignForm.total_payment}
                    onChange={(e) => setAssignForm({ ...assignForm, total_payment: e.target.value })}
                    placeholder="0.00"
                  />
                  <div className="form-hint">Total amount the engineer will be paid for this project</div>
                </div>
              ) : selectedProject.project_type === 'fixed_monthly' ? (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Monthly Pay ($)</label>
                    <input
                      className="form-input"
                      type="number"
                      step="0.01"
                      value={assignForm.monthly_pay}
                      onChange={(e) => setAssignForm({ ...assignForm, monthly_pay: e.target.value })}
                      placeholder="0.00"
                    />
                    <div className="form-hint">Amount paid to engineer each month</div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Monthly Bill ($)</label>
                    <input
                      className="form-input"
                      type="number"
                      step="0.01"
                      value={assignForm.monthly_bill}
                      onChange={(e) => setAssignForm({ ...assignForm, monthly_bill: e.target.value })}
                      placeholder="0.00"
                    />
                    <div className="form-hint">Amount billed to customer each month</div>
                  </div>
                </div>
              ) : (
                <>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Pay Rate ($/hr)</label>
                    <input
                      className="form-input"
                      type="number"
                      step="0.01"
                      value={assignForm.pay_rate}
                      onChange={(e) => setAssignForm({ ...assignForm, pay_rate: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Bill Rate ($/hr)</label>
                    <input
                      className="form-input"
                      type="number"
                      step="0.01"
                      value={assignForm.bill_rate}
                      onChange={(e) => setAssignForm({ ...assignForm, bill_rate: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Max Hours</label>
                    <input
                      className="form-input"
                      type="number"
                      step="0.5"
                      value={assignForm.max_hours}
                      onChange={(e) => setAssignForm({ ...assignForm, max_hours: e.target.value })}
                      placeholder="0 = unlimited"
                    />
                    <div className="form-hint">Leave 0 or blank for no limit</div>
                  </div>
                </div>
                {selectedProject.overtime_type && selectedProject.overtime_type !== 'none' && (
                <div className="form-row" style={{ marginTop: 4 }}>
                  <div className="form-group">
                    <label className="form-label" style={{ color: '#d97706' }}>OT Pay Rate ($/hr)</label>
                    <input
                      className="form-input"
                      type="number"
                      step="0.01"
                      value={assignForm.ot_pay_rate}
                      onChange={(e) => setAssignForm({ ...assignForm, ot_pay_rate: e.target.value })}
                      placeholder="0.00"
                    />
                    <div className="form-hint">Amount paid to engineer for overtime hours</div>
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ color: '#d97706' }}>OT Bill Rate ($/hr)</label>
                    <input
                      className="form-input"
                      type="number"
                      step="0.01"
                      value={assignForm.ot_bill_rate}
                      onChange={(e) => setAssignForm({ ...assignForm, ot_bill_rate: e.target.value })}
                      placeholder="0.00"
                    />
                    <div className="form-hint">Amount charged to customer for overtime hours</div>
                  </div>
                </div>
                )}
                </>
              )}
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? 'Adding...' : 'Add to Project'}
              </button>
            </form>
          </div>
        </Modal>
      )}

      {emailPreview && (
        <Modal title="Email Preview" onClose={() => setEmailPreview(null)} width={640}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}><strong>To:</strong> {emailPreview.to}</div>
            <div style={{ fontSize: 13, color: '#64748b' }}><strong>Subject:</strong> {emailPreview.subject}</div>
          </div>
          <div
            style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, background: '#fff', maxHeight: 400, overflowY: 'auto' }}
            dangerouslySetInnerHTML={{ __html: emailPreview.html }}
          />
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setEmailPreview(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSendNotification}>Send Email</button>
          </div>
        </Modal>
      )}
      {modal === 'reassign' && reassignProject && (
        <Modal title={`Reassign Timesheets — ${reassignProject.name}`} onClose={() => setModal(null)} width={700}>
          <div style={{ marginBottom: 12 }}>
            <label className="form-label">Move selected timesheets to:</label>
            <select className="form-select" value={reassignTarget} onChange={e => setReassignTarget(e.target.value)}>
              <option value="">— Select destination project —</option>
              {projects.filter(p => p.id !== reassignProject.id && p.status === 'active').map(p => (
                <option key={p.id} value={p.id}>{p.name} {p.po_number ? `(PO: ${p.po_number})` : ''}</option>
              ))}
            </select>
          </div>
          {reassignTimesheets.length === 0 ? (
            <p style={{ color: '#64748b', fontSize: 14 }}>No timesheets found for this project.</p>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: '#475569' }}>{reassignSelected.length} of {reassignTimesheets.length} selected</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn btn-secondary" style={{ fontSize: 11, padding: '2px 8px' }}
                    onClick={() => setReassignSelected(reassignTimesheets.map(t => t.id))}>Select All</button>
                  <button type="button" className="btn btn-secondary" style={{ fontSize: 11, padding: '2px 8px' }}
                    onClick={() => setReassignSelected([])}>Deselect All</button>
                </div>
              </div>
              <div className="table-wrap" style={{ maxHeight: 350, overflowY: 'auto' }}>
                <table style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 30 }}></th>
                      <th>Engineer</th>
                      <th>Week Ending</th>
                      <th>Hours</th>
                      <th>Status</th>
                      <th>Invoiced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reassignTimesheets.map(ts => (
                      <tr key={ts.id} style={{ opacity: ts.invoice_id ? 0.5 : 1 }}>
                        <td>
                          <input type="checkbox" checked={reassignSelected.includes(ts.id)} disabled={!!ts.invoice_id}
                            onChange={e => {
                              if (e.target.checked) {
                                setReassignSelected([...reassignSelected, ts.id]);
                              } else {
                                setReassignSelected(reassignSelected.filter(id => id !== ts.id));
                              }
                            }} />
                        </td>
                        <td>{ts.engineer_name}</td>
                        <td>{new Date(ts.week_ending + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                        <td style={{ textAlign: 'right' }}>{parseFloat(ts.total_hours || 0).toFixed(1)}</td>
                        <td>
                          <span className="badge" style={{
                            background: ts.status === 'approved' ? '#dcfce7' : ts.status === 'submitted' ? '#dbeafe' : '#f1f5f9',
                            color: ts.status === 'approved' ? '#166534' : ts.status === 'submitted' ? '#1e40af' : '#475569'
                          }}>{ts.status}</span>
                        </td>
                        <td style={{ fontSize: 12, color: ts.invoice_id ? '#dc2626' : '#64748b' }}>
                          {ts.invoice_id ? `Inv #${ts.invoice_id}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {reassignTimesheets.some(ts => ts.invoice_id) && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px', marginTop: 8, fontSize: 12, color: '#991b1b' }}>
                  Timesheets already linked to an invoice are grayed out and cannot be moved. Void the invoice first if you need to reassign them.
                </div>
              )}
            </>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleReassign}
              disabled={reassigning || !reassignTarget || reassignSelected.length === 0}>
              {reassigning ? 'Moving...' : `Move ${reassignSelected.length} Timesheet(s)`}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
